import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function AdminAccess() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [error, setError] = useState("");
  const load = async () => {
    try { setRows((await api.get("/admin/access")).data); setError(""); }
    catch { setError("Could not load admin access. Please try again."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const add = async (event) => {
    event.preventDefault(); setBusy(true); setError("");
    try {
      await api.post("/admin/access", { email: email.trim().toLowerCase() });
      setEmail(""); await load(); toast.success("Admin access granted");
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Enter a valid email address.");
    } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true); setError("");
    try {
      await api.delete(`/admin/access/${encodeURIComponent(removing.email)}`);
      const self = removing.email === user.email.toLowerCase();
      setRemoving(null); toast.success("Admin access removed");
      if (self) { await checkAuth(); navigate("/signin?tab=organizer", { replace: true }); }
      else await load();
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not remove access.");
      setRemoving(null);
    } finally { setBusy(false); }
  };
  return (
    <section className="max-w-4xl" data-testid="admin-access-page">
      <div className="flex items-center gap-3"><ShieldCheck className="w-8 h-8 text-[#B36906]" /><h1 className="font-display font-black text-3xl">Admin Access</h1></div>
      <p className="mt-3 text-sm text-[#7A7D8C]">Choose which Google accounts can enter the organizer console. Changes take effect immediately.</p>
      <form onSubmit={add} className="mt-8 rounded-2xl border border-slate-200 bg-white p-5" data-testid="admin-access-form">
        <label htmlFor="admin-email" className="text-sm font-semibold">Google account email</label>
        <div className="mt-2 flex flex-col sm:flex-row gap-3">
          <Input id="admin-email" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} placeholder="Email address" data-testid="admin-access-email" />
          <Button disabled={busy} type="submit" className="rounded-full bg-[#FF9F1C] hover:bg-[#B36906]" data-testid="admin-access-add"><Plus className="w-4 h-4 mr-2" />{busy ? "Saving…" : "Add Admin"}</Button>
        </div>
      </form>
      {error && <div role="alert" className="mt-4 text-sm text-red-700" data-testid="admin-access-error">{error}<button className="ml-3 underline" onClick={load} data-testid="admin-access-retry">Reload list</button></div>}
      {loading ? <p className="mt-6 text-sm" data-testid="admin-access-loading">Loading authorized admins…</p> : (
        <div className="mt-6 space-y-3" data-testid="admin-access-list">
          <p className="text-sm text-[#7A7D8C]" data-testid="admin-access-count">{rows.length} authorized {rows.length === 1 ? "admin" : "admins"}</p>
          {rows.map((row) => <div key={row.email} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-white p-5" data-testid={`admin-access-row-${row.email}`}>
            <div className="min-w-0"><div className="font-semibold break-all" data-testid={`admin-access-email-${row.email}`}>{row.email}{row.email === user?.email?.toLowerCase() && <span className="ml-2 text-xs text-[#7A7D8C]">You</span>}</div><span className="mt-1 inline-block text-xs font-semibold text-teal-700" data-testid={`admin-access-status-${row.email}`}>Active · Authorized</span></div>
            <Button variant="outline" disabled={busy || rows.length === 1} onClick={() => setRemoving(row)} className="rounded-full text-red-700" data-testid={`admin-access-remove-${row.email}`}><Trash2 className="w-4 h-4 mr-2" />Remove Access</Button>
          </div>)}
          {rows.length === 1 && <p className="text-xs text-[#7A7D8C]" data-testid="admin-access-last-admin">At least one admin must remain. Add another admin before removing your access.</p>}
        </div>
      )}
      <Dialog open={!!removing} onOpenChange={(open) => { if (!open && !busy) setRemoving(null); }}>
        <DialogContent data-testid="admin-access-confirm" className="rounded-2xl">
          <DialogHeader><DialogTitle>Remove admin access?</DialogTitle><DialogDescription>{removing?.email} will lose access to the Admin Panel, including any active sessions.</DialogDescription></DialogHeader>
          <div className="flex justify-end gap-3"><Button variant="outline" disabled={busy} onClick={() => setRemoving(null)} data-testid="admin-access-cancel">Cancel</Button><Button disabled={busy} className="bg-red-700 hover:bg-red-800" onClick={remove} data-testid="admin-access-confirm-remove">{busy ? "Removing…" : "Remove Access"}</Button></div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
